import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Clock,
  Download,
  Eye,
  FileText,
  Lock,
  RefreshCw,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { EbookLargeCover } from '@/components/ebook/EbookLargeCover';
import { EbookShareButtons } from '@/components/ebook/EbookShareButtons';
import { EbookFAQ } from '@/components/ebook/EbookFAQ';
import { useEbookClaim } from '@/components/ebook/useEbookClaim';
import { trackEbookEvent } from '@/lib/ebookAnalytics';
import { createCheckoutSession, verifyCheckoutSession } from '@/lib/stripeCheckout';
import type { AccessState, Ebook, EbookChapter } from '@/types/ebook';
import { EmailGateModal } from '@/components/ebook/EmailGateModal';
import { N4PublicNav } from '@/components/n4/N4PublicNav';

export default function EbookDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ctaRef = useRef<HTMLButtonElement | null>(null);

  const [ebook, setEbook] = useState<Ebook | null | undefined>(undefined);
  const [chapters, setChapters] = useState<EbookChapter[]>([]);
  const [accessState, setAccessState] = useState<AccessState | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const { claiming, claimFreeEbook } = useEbookClaim();

  // Refetch access state (called after Stripe success)
  const refetchAccess = async (ebookId: string) => {
    const { data } = await (supabase as any).rpc('can_access_ebook', {
      p_ebook_id: ebookId,
      p_user_id: user?.id ?? null,
    });
    setAccessState(((data as string) ?? 'purchase_required') as AccessState);
  };

  // Handle Stripe Checkout return (DV-45): verify, then poll access before giving up
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [verifyPhase, setVerifyPhase] = useState<'idle' | 'verifying' | 'timeout'>('idle');
  const verifyRunRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopVerification = () => {
    verifyRunRef.current += 1;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  const runVerification = async (sessionId: string) => {
    if (!slug || !ebook || !user) return;
    stopVerification();
    const runId = verifyRunRef.current;
    const alive = () => verifyRunRef.current === runId;
    const ebookId = ebook.id;
    const userId = user.id;
    setVerifyPhase('verifying');

    const onSuccess = () => {
      if (!alive()) return;
      stopVerification();
      toast.success('ชำระเงินสำเร็จ! เริ่มอ่านได้เลย');
      setAccessState('full_access');
      setVerifyPhase('idle');
      setPendingSessionId(null);
      window.history.replaceState({}, '', `/ebook/${slug}`);
      setTimeout(() => navigate(`/ebook/${slug}/read/1`), 1000);
    };

    // Step A
    try {
      const result = await verifyCheckoutSession(sessionId);
      if (!alive()) return;
      if (result.status === 'paid' && result.claimed) return onSuccess();
    } catch {
      if (!alive()) return;
    }

    // Step B: poll access (webhook grants it server-side)
    const MAX_TRIES = 10;
    const poll = async (attempt: number) => {
      if (!alive()) return;
      try {
        const { data } = await (supabase as any).rpc('can_access_ebook', {
          p_ebook_id: ebookId,
          p_user_id: userId,
        });
        if (!alive()) return;
        if (data === 'full_access') return onSuccess();
      } catch {
        if (!alive()) return;
      }
      if (attempt >= MAX_TRIES) {
        setVerifyPhase('timeout');
        return;
      }
      pollTimerRef.current = setTimeout(() => poll(attempt + 1), 3000);
    };
    pollTimerRef.current = setTimeout(() => poll(1), 3000);
  };

  useEffect(() => {
    if (!slug || !ebook || !user?.id) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('payment') !== 'success' || !sessionId) return;
    setPendingSessionId(sessionId);
    void runVerification(sessionId);
    return () => stopVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ebook?.id, slug, user?.id]);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await (supabase as any)
        .from('ebooks')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      setEbook((data ?? null) as Ebook | null);
    })();
  }, [slug]);

  useEffect(() => {
    if (!ebook) return;
    (async () => {
      const [chRes, accessRes] = await Promise.all([
        (supabase as any)
          .from('ebook_chapters')
          .select('chapter_number, title_th, reading_time_minutes, is_free_preview')
          .eq('ebook_id', ebook.id)
          .order('chapter_number'),
        (supabase as any).rpc('can_access_ebook', {
          p_ebook_id: ebook.id,
          p_user_id: user?.id ?? null,
        }),
      ]);
      setChapters((chRes.data ?? []) as EbookChapter[]);
      const state = (accessRes.data as string) ?? 'purchase_required';
      setAccessState(state as AccessState);
    })();
  }, [ebook, user]);

  // GA4 detail_view once ebook loads
  useEffect(() => {
    if (!ebook) return;
    trackEbookEvent('ebook_detail_view', {
      slug: ebook.slug,
      price: ebook.price_thb ?? 0,
      access_type: ebook.access_type,
    });
  }, [ebook]);

  const ch1Free = useMemo(() => chapters.find((c) => c.chapter_number === 1)?.is_free_preview, [chapters]);
  const chapterCount = chapters.length;
  const canRead = chapterCount > 0;
  const canDownload = !!ebook?.pdf_storage_path;
  const isPdfOnly = chapterCount === 0 && canDownload;

  const openSignedPdf = async () => {
    if (!ebook?.id) return;
    try {
      const { data, error } = await supabase.functions.invoke('ebook-download', {
        body: { ebook_id: ebook.id, sample: true },
      });
      if (error || !data?.signedUrl) throw error ?? new Error('no url');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('signed url error:', e);
      toast.error('ยังไม่มีไฟล์ตัวอย่างสำหรับเล่มนี้');
    }
  };


  const openEbookPdf = async () => {
    if (!ebook?.id) return;
    try {
      const { data, error } = await supabase.functions.invoke('ebook-download', {
        body: { ebook_id: ebook.id },
      });
      if (error || !data?.signedUrl) throw error ?? new Error('no url');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('ebook-download error:', e);
      toast.error('ไม่สามารถเปิดไฟล์ได้ ลองอีกครั้ง');
    }
  };


  if (ebook === undefined) {
    return (
      <div className="min-h-screen" style={{ background: '#0A1A26' }}>
        <div className="max-w-[960px] mx-auto py-8 px-4">
          <div
            className="animate-pulse"
            style={{ height: 260, borderRadius: 12, background: 'rgba(5,242,242,0.04)' }}
          />
        </div>
      </div>
    );
  }

  if (ebook === null || accessState === 'not_found') {
    return (
      <div className="min-h-screen" style={{ background: '#0A1A26' }}>
        <div className="max-w-[640px] mx-auto py-16 px-4 text-center">
          <h1 className="text-[20px] font-medium mb-3" style={{ color: '#F0F8FF' }}>
            ไม่พบ ebook นี้
          </h1>
          <Link to="/ebook" className="text-[12px]" style={{ color: '#05F2F2' }}>
            ← กลับไปหน้า AIM Books
          </Link>
        </div>
      </div>
    );
  }

  const hrs = ebook.page_count ? Math.max(1, Math.ceil(ebook.page_count / 30)) : null;

  const claimFree = async () => {
    trackEbookEvent('ebook_free_claim', { slug: ebook.slug });
    if (!user) {
      navigate(`/login?redirect=/ebook/${slug}`);
      return;
    }
    const ok = await claimFreeEbook(ebook.id, user.id);
    if (ok) navigate(`/ebook/${slug}/read/1`);
  };

  const openPayment = async () => {
    trackEbookEvent('ebook_purchase_click', { slug: ebook.slug, price: ebook.price_thb ?? 0 });
    if (!user) {
      navigate(`/login?redirect=/ebook/${slug}`);
      return;
    }
    setCheckoutLoading(true);
    try {
      const { url } = await createCheckoutSession({
        ebook_id: ebook.id,
        ebook_slug: slug!,
      });
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Stripe checkout error:', err);
      if (err instanceof Error && err.message === 'AUTH_REQUIRED') {
        navigate(`/login?redirect=/ebook/${slug}`);
        setCheckoutLoading(false);
        return;
      }
      toast.error('ไม่สามารถเปิดหน้าชำระเงินได้ กรุณาลองใหม่');
      setCheckoutLoading(false);
    }
  };

  type CtaConfig = { label: string; onClick: () => void; variant: 'orange' | 'green'; disabled?: boolean };
  const cta = ((): CtaConfig => {
    if (verifyPhase === 'verifying' && accessState !== 'full_access') {
      return { label: 'กำลังยืนยันการชำระเงิน…', variant: 'orange', onClick: () => {}, disabled: true };
    }
    // PDF-only overrides
    if (isPdfOnly) {
      if (accessState === 'full_access') {
        return {
          label: 'ดาวน์โหลด PDF',
          variant: 'orange',
          onClick: () => ebook.pdf_storage_path && openEbookPdf(),
        };
      }
      if (ebook.access_type === 'free') {
        return {
          label: 'ดาวน์โหลดฟรี',
          variant: 'green',
          onClick: () => {
            if (user && ebook.pdf_storage_path) {
              openEbookPdf();
            } else {
              setEmailGateOpen(true);
            }
          },
        };
      }
      if (ebook.access_type === 'subscriber' && accessState === 'upgrade_required') {
        return { label: 'อัปเกรดเป็น Power+', variant: 'orange', onClick: () => navigate('/pricing') };
      }
      return {
        label: checkoutLoading ? 'กำลังโหลด...' : `ซื้อ ฿${ebook.price_thb ?? 0}`,
        variant: 'orange',
        onClick: openPayment,
        disabled: checkoutLoading,
      };
    }

    if (accessState === 'full_access') {
      return {
        label: 'อ่านเลย',
        variant: 'orange',
        onClick: () => navigate(`/ebook/${slug}/read/1`),
      };
    }
    if (accessState === 'login_required') {
      return {
        label: 'เข้าสู่ระบบเพื่ออ่าน',
        variant: 'orange',
        onClick: () => navigate(`/login?redirect=/ebook/${slug}`),
      };
    }
    if (ebook.access_type === 'free') {
      return {
        label: 'เริ่มอ่านฟรี',
        variant: 'green',
        onClick: () => navigate(`/ebook/${slug}/read/1`),
      };
    }
    if (ebook.access_type === 'subscriber' && accessState === 'upgrade_required') {
      return { label: 'อัปเกรดเป็น Power+', variant: 'orange', onClick: () => navigate('/pricing') };
    }
    if (accessState === 'free_preview') {
      return {
        label: 'อ่านบทที่ 1 ฟรี',
        variant: 'orange',
        onClick: () => navigate(`/ebook/${slug}/read/1`),
      };
    }
    return {
      label: checkoutLoading ? 'กำลังโหลด...' : `ซื้อ ฿${ebook.price_thb ?? 0}`,
      variant: 'orange',
      onClick: openPayment,
      disabled: checkoutLoading,
    };
  })();

  const ctaStyle: React.CSSProperties =
    cta.variant === 'green'
      ? {
          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
          color: '#fff',
          borderRadius: 10,
          boxShadow: '0 0 20px rgba(74,222,128,0.2)',
        }
      : {
          background: 'linear-gradient(135deg, #F27405, #D45E00)',
          color: '#fff',
          borderRadius: 10,
          boxShadow: '0 0 20px rgba(242,116,5,0.2)',
        };

  const scrollToCta = () =>
    ctaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <>
      <Helmet>
        <title>{`${ebook.title_th} | AIM Books`}</title>
        <meta property="og:site_name" content="AIM Academy" />
        <meta name="description" content={ebook.subtitle_th ?? ''} />
        <meta property="og:title" content={`${ebook.title_th} — AIM Books`} />
        <meta property="og:description" content={ebook.subtitle_th ?? ''} />
        <meta property="og:type" content="book" />
        <meta property="og:url" content={`https://aimacademy.co/ebook/${ebook.slug}`} />
        {ebook.og_image_url && (
          <meta property="og:image" content={ebook.og_image_url} />
        )}
        {ebook.og_image_url && <meta property="og:image:width" content="1200" />}
        {ebook.og_image_url && <meta property="og:image:height" content="630" />}
        <meta property="book:author" content="Dr. Kim" />
        <link rel="canonical" href={`https://aimacademy.co/ebook/${ebook.slug}`} />
      </Helmet>
    <div className="min-h-screen" style={{ background: '#0A1A26' }}>
<N4PublicNav />
      <div className="max-w-[960px] mx-auto py-6 px-4" style={{ paddingBottom: 120 }}>
        <Link to="/ebook" className="inline-flex items-center gap-1.5 mb-5 text-[12px]" style={{ color: '#05F2F2' }}>
          <ArrowLeft size={14} />
          AIM Books
        </Link>

        {/* Section 1: Hero — Proof First */}
        <section className="grid gap-6 mb-10 md:grid-cols-[1fr_320px] max-md:grid-cols-1">
          {/* Left: content */}
          <div className="min-w-0 max-md:order-2">
            <h1 className="text-[22px] font-medium leading-snug mb-4" style={{ color: '#F0F8FF' }}>
              {ebook.title_th}
            </h1>

            {ebook.testimonial_quote ? (
              <div className="mb-4">
                <p
                  className="leading-snug"
                  style={{ fontSize: 30, fontWeight: 600, color: '#F0F8FF' }}
                >
                  「{ebook.testimonial_quote}」
                </p>
                {ebook.testimonial_author && (
                  <div style={{ fontSize: 13, color: '#6593A6', marginTop: 8 }}>
                    — {ebook.testimonial_author}
                  </div>
                )}
                {ebook.subtitle_th && (
                  <p className="text-[13px] mt-3" style={{ color: '#6593A6' }}>
                    {ebook.subtitle_th}
                  </p>
                )}
              </div>
            ) : (
              ebook.subtitle_th && (
                <p className="text-[16px] mb-4" style={{ color: '#6593A6' }}>
                  {ebook.subtitle_th}
                </p>
              )
            )}

            <div className="flex flex-wrap gap-3 text-[12px] mb-4" style={{ color: '#3D6D82' }}>
              {hrs && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> ~{hrs} ชม.
                </span>
              )}
              {ebook.page_count != null && (
                <span className="inline-flex items-center gap-1">
                  <FileText size={12} /> {ebook.page_count} หน้า
                </span>
              )}
              {ebook.download_count >= 10 && (
                <span className="inline-flex items-center gap-1">
                  <Users size={12} /> {ebook.download_count} คนอ่านแล้ว
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3" style={{ fontSize: 11, fontWeight: 600, color: '#05F2F2', letterSpacing: 0.3 }}>
              {canRead && (
                <span className="inline-flex items-center gap-1">
                  <Eye size={11} /> อ่านออนไลน์
                </span>
              )}
              {canDownload && (
                <span className="inline-flex items-center gap-1">
                  <Download size={11} /> PDF
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <RefreshCw size={11} /> อัปเดตฟรี
              </span>
            </div>
          </div>

          {/* Right: cover in card frame + secondary buttons */}
          <div className="max-md:order-1 flex flex-col items-center">
            <div
              className="w-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(145deg,#012840 0%,#0A1A26 100%)',
                border: '1px solid rgba(5,242,242,0.15)',
                boxShadow: '0 0 12px rgba(5,242,242,0.05)',
                borderRadius: 12,
                padding: 24,
              }}
            >
              <EbookLargeCover preset={ebook.cover_image_url} title={ebook.title_th} />
            </div>

            {/* Secondary: 2-format download */}
            {!isPdfOnly && canRead && canDownload && ebook.pdf_storage_path &&
              (accessState === 'full_access' ||
                (accessState === 'free_preview' && ebook.access_type === 'free')) && (
                <button
                  type="button"
                  onClick={() => {
                    if (accessState === 'full_access' || user) {
                      openEbookPdf();
                    } else {
                      setEmailGateOpen(true);
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 transition-all hover:brightness-110"
                  style={{
                    marginTop: 12,
                    background: 'rgba(5,242,242,0.05)',
                    border: '1px solid rgba(5,242,242,0.2)',
                    color: '#05F2F2',
                    borderRadius: 10,
                    padding: 12,
                    fontSize: 14,
                    fontWeight: 500,
                    minHeight: 44,
                  }}
                >
                  <Download size={16} />
                  ดาวน์โหลด PDF
                </button>
              )}

            {/* Secondary: PDF-only paid preview */}
            {isPdfOnly && ebook.access_type === 'paid' && accessState !== 'full_access' &&
              ebook.pdf_sample_path && (
                <button
                  type="button"
                  onClick={() => openSignedPdf()}
                  className="w-full inline-flex items-center justify-center gap-2 transition-all hover:brightness-110"
                  style={{
                    marginTop: 12,
                    background: 'rgba(5,242,242,0.05)',
                    border: '1px solid rgba(5,242,242,0.2)',
                    color: '#05F2F2',
                    borderRadius: 10,
                    padding: 12,
                    fontSize: 14,
                    fontWeight: 500,
                    minHeight: 44,
                  }}
                >
                  <Eye size={16} />
                  ดูตัวอย่าง
                </button>
              )}
          </div>
        </section>

        {/* Section 2: Author Trust Card */}
        <section
          className="mb-10"
          style={{
            background: 'linear-gradient(145deg,#012840 0%,#0A1A26 100%)',
            border: '1px solid rgba(5,242,242,0.15)',
            boxShadow: '0 0 12px rgba(5,242,242,0.05)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#F27405,#D45E00)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              DK
            </div>
            <div className="flex-1 min-w-[180px]">
              <div style={{ fontSize: 14, fontWeight: 500, color: '#D0E4EE' }}>ผู้เขียน: Dr. Kim</div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    background: 'rgba(5,242,242,0.1)',
                    color: '#05F2F2',
                    border: '1px solid rgba(5,242,242,0.2)',
                  }}
                >
                  สอน AI ให้ SME ไทย 200+ ธุรกิจ
                </span>
                {ebook.download_count >= 10 && (
                  <span
                    className="inline-flex items-center gap-1"
                    style={{
                      padding: '3px 10px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: 'rgba(5,242,242,0.1)',
                      color: '#05F2F2',
                      border: '1px solid rgba(5,242,242,0.2)',
                    }}
                  >
                    <Users size={11} />
                    {ebook.download_count} คนอ่านแล้ว
                  </span>
                )}
              </div>
            </div>
            <a
              href="https://lin.ee/drgim"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#05F2F2' }}
            >
              ดูโปรไฟล์ผู้เขียน
            </a>
          </div>
        </section>

        {/* Section 3: สิ่งที่คุณจะได้ */}
        {chapters.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4" style={{ fontSize: 15, fontWeight: 500, color: '#F27405' }}>
              สิ่งที่คุณจะได้
            </h2>
            <div
              className={
                Array.isArray(ebook.bullets_th) && ebook.bullets_th.length > 0
                  ? 'grid gap-3 md:grid-cols-[1fr_1fr_280px] max-md:grid-cols-1'
                  : 'grid gap-3 md:grid-cols-2 max-md:grid-cols-1'
              }
            >
              {chapters.map((c) => {
                const chCanRead = c.is_free_preview || accessState === 'full_access';
                const handle = () => {
                  if (chCanRead) navigate(`/ebook/${slug}/read/${c.chapter_number}`);
                  else {
                    toast.info('ปลดล็อกได้ที่แถบด้านล่าง');
                    scrollToCta();
                  }
                };
                return (
                  <button
                    key={c.chapter_number}
                    onClick={handle}
                    title={!chCanRead ? 'ซื้อเพื่ออ่านบทนี้' : undefined}
                    className="text-left transition-all hover:brightness-110 relative"
                    style={{
                      background: 'linear-gradient(145deg,#012840 0%,#0A1A26 100%)',
                      border: '1px solid rgba(5,242,242,0.15)',
                      boxShadow: '0 0 12px rgba(5,242,242,0.05)',
                      borderRadius: 12,
                      padding: 14,
                    }}
                  >
                    {!c.is_free_preview && !chCanRead && (
                      <Lock
                        size={12}
                        style={{ position: 'absolute', top: 12, right: 12, color: '#3D6D82' }}
                      />
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#05F2F2',
                        letterSpacing: 0.5,
                      }}
                    >
                      บทที่ {c.chapter_number}
                    </div>
                    <div
                      className="mt-1"
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#D0E4EE',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {c.title_th}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ fontSize: 12, color: '#3D6D82' }}
                      >
                        <Clock size={12} />~{c.reading_time_minutes ?? 0} น.
                      </span>
                      {c.is_free_preview && (
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 600,
                            background: 'rgba(74,222,128,0.1)',
                            color: '#4ADE80',
                            border: '1px solid rgba(74,222,128,0.3)',
                          }}
                        >
                          อ่านฟรี
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {Array.isArray(ebook.bullets_th) && ebook.bullets_th.length > 0 && (
                <aside
                  className="md:row-span-full md:col-start-3"
                  style={{
                    background: 'linear-gradient(145deg,#012840 0%,#0A1A26 100%)',
                    border: '1px solid rgba(5,242,242,0.15)',
                    boxShadow: '0 0 12px rgba(5,242,242,0.05)',
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#F29D52', marginBottom: 12 }}>
                    เจาะลึกเนื้อหา
                  </div>
                  <ul className="flex flex-col gap-2.5">
                    {ebook.bullets_th.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check size={14} style={{ color: '#4ADE80', marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: '#D0E4EE', lineHeight: 1.5 }}>{b}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
            </div>
          </section>
        )}

        {/* FAQ */}
        {ebook.faq_items && ebook.faq_items.length > 0 && (
          <section
            className="my-8"
            style={{
              background: 'linear-gradient(145deg,#012840 0%,#0A1A26 100%)',
              border: '1px solid rgba(5,242,242,0.15)',
              borderRadius: 12,
              padding: 20,
            }}
          >
            <EbookFAQ items={ebook.faq_items} />
          </section>
        )}

        {/* Footer + Share */}
        <div className="text-center py-6" style={{ fontSize: 12, color: '#3D6D82' }}>
          เนื้อหาเดียวกับที่สอนใน workshop — รวมไว้ที่เดียว อ่านกี่รอบก็ได้
        </div>
        <div className="pb-4">
          <div style={{ fontSize: 12, color: '#6593A6', marginBottom: 8 }}>แชร์ให้เพื่อน</div>
          <EbookShareButtons
            url={`https://aimacademy.co/ebook/${ebook.slug}`}
            title={ebook.title_th}
          />
        </div>
      </div>

      {/* Sticky Bottom Buy Bar */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 40,
          background: 'rgba(1,40,64,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(5,242,242,0.15)',
          padding: '12px 16px',
        }}
      >
        {verifyPhase === 'timeout' && accessState !== 'full_access' && (
          <div
            role="status"
            className="max-w-[960px] mx-auto mb-3"
            style={{
              background: 'linear-gradient(145deg, #012840 0%, #0A1A26 100%)',
              border: '1px solid rgba(242,116,5,0.4)',
              borderRadius: 12,
              padding: 14,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 500, color: '#F29D52' }}>
              ระบบยังยืนยันการชำระเงินไม่เสร็จ
            </div>
            <p style={{ fontSize: 13, color: '#6593A6', marginTop: 4, lineHeight: 1.6 }}>
              ถ้าคุณชำระเงินแล้ว ไม่ต้องจ่ายซ้ำนะครับ — บางครั้ง PromptPay ใช้เวลาสักครู่ กด 'ตรวจสอบอีกครั้ง' ได้เลย
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => pendingSessionId && runVerification(pendingSessionId)}
                className="inline-flex items-center justify-center gap-2 transition-all hover:brightness-110 max-md:w-full"
                style={{
                  background: 'linear-gradient(135deg, #F27405, #D45E00)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  height: 44,
                  padding: '0 18px',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <RefreshCw size={15} /> ตรวจสอบอีกครั้ง
              </button>
              <a
                href="https://lin.ee/qra6yHn"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:brightness-125 max-md:w-full max-md:text-center"
                style={{ fontSize: 13, color: '#05F2F2' }}
              >
                ติดต่อทีมงานทาง LINE @drgim
              </a>
            </div>
          </div>
        )}
        <div className="max-w-[960px] mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0 flex-shrink">
            {ebook.access_type === 'free' ? (
              <div style={{ fontSize: 18, fontWeight: 500, color: '#4ADE80' }}>ฟรี</div>
            ) : ebook.access_type === 'subscriber' ? (
              <div style={{ fontSize: 14, color: '#05F2F2' }}>รวมในแพลน Power+</div>
            ) : (
              <>
                <div style={{ fontSize: 20, fontWeight: 500, color: '#F27405' }}>
                  ฿{ebook.price_thb ?? 0}
                </div>
                <div style={{ fontSize: 11, color: '#6593A6' }}>จ่ายครั้งเดียว อ่านได้ตลอด</div>
              </>
            )}
            {!isPdfOnly && ch1Free && accessState !== 'full_access' && cta.label !== 'อ่านบทที่ 1 ฟรี' && (
              <button
                type="button"
                onClick={() => navigate(`/ebook/${slug}/read/1`)}
                className="max-md:hidden hover:brightness-125"
                style={{
                  marginTop: 4,
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: '#05F2F2',
                  fontSize: 12,
                  textAlign: 'left',
                }}
              >
                อ่านบทที่ 1 ฟรีก่อนตัดสินใจ
              </button>
            )}
          </div>
          <button
            ref={ctaRef}
            onClick={cta.onClick}
            disabled={cta.disabled}
            className="transition-all hover:brightness-110 disabled:opacity-60 max-md:flex-1"
            style={{
              ...ctaStyle,
              height: 48,
              fontSize: 15,
              fontWeight: 600,
              minWidth: 180,
              paddingLeft: 20,
              paddingRight: 20,
            }}
          >
            {cta.label}
          </button>
        </div>
      </div>

    </div>
    {ebook.pdf_storage_path && (
      <EmailGateModal
        open={emailGateOpen}
        onOpenChange={setEmailGateOpen}
        ebookId={ebook.id}
      />
    )}
    </>
  );
}

